import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Calendar, Clock, Plus, ArrowLeft, User, 
  CheckCircle, XCircle, AlertCircle, Filter, MapPin
} from 'lucide-react';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

const SERVICE_TYPES = []; // Will be fetched from Setmore

const STAFF_LOCATIONS = []; // Will be fetched from Setmore

export default function Bookings() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [integrationStatus, setIntegrationStatus] = useState(null);
  const [services, setServices] = useState([]);
  const [staffLocations, setStaffLocations] = useState([]);
  
  const [formData, setFormData] = useState({
    child_id: '',
    service_key: '',
    staff_key: '',
    scheduled_date: '',
    scheduled_time: '',
    duration_minutes: 30,
    notes: '',
    customer_email: '',
    customer_phone: ''
  });

  const loadData = useCallback(async () => {
    try {
      const [bookingsRes, childrenRes, statusRes, servicesRes, staffRes] = await Promise.all([
        axios.get(`${API}/bookings`),
        axios.get(`${API}/children`),
        axios.get(`${API}/integrations/status`),
        axios.get(`${API}/setmore/services`),
        axios.get(`${API}/setmore/staff`)
      ]);
      setBookings(bookingsRes.data.bookings);
      setChildren(childrenRes.data);
      setIntegrationStatus(statusRes.data?.setmore);
      setServices(servicesRes.data?.services || []);
      setStaffLocations(staffRes.data?.staff || []);
      
      // Set default values if available
      if (servicesRes.data?.services?.length > 0) {
        setFormData(prev => ({ ...prev, service_key: servicesRes.data.services[0].key }));
      }
      if (staffRes.data?.staff?.length > 0) {
        setFormData(prev => ({ ...prev, staff_key: staffRes.data.staff[0].key }));
      }
    } catch (error) {
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const createBooking = async (e) => {
    e.preventDefault();
    try {
      const scheduledDateTime = new Date(`${formData.scheduled_date}T${formData.scheduled_time}`);
      const selectedService = services.find(s => s.key === formData.service_key);
      
      await axios.post(`${API}/bookings`, {
        child_id: formData.child_id,
        service_key: formData.service_key,
        staff_key: formData.staff_key,
        scheduled_date: scheduledDateTime.toISOString(),
        duration_minutes: selectedService?.duration || formData.duration_minutes,
        notes: formData.notes,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone
      });
      
      toast.success('Booking created successfully');
      setShowModal(false);
      setFormData({
        child_id: '',
        service_key: services[0]?.key || '',
        staff_key: staffLocations[0]?.key || '',
        scheduled_date: '',
        scheduled_time: '',
        duration_minutes: 30,
        notes: '',
        customer_email: '',
        customer_phone: ''
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    }
  };

  const cancelBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) return;
    try {
      await axios.delete(`${API}/bookings/${bookingId}`);
      toast.success('Booking cancelled');
      loadData();
    } catch (error) {
      toast.error('Failed to cancel booking');
    }
  };

  const getChildName = (childId) => {
    const child = children.find(c => c.id === childId);
    return child ? `${child.first_name} ${child.last_name}` : 'Unknown';
  };

  const getLocationName = (staffKey) => {
    const location = staffLocations.find(l => l.key === staffKey);
    return location?.name || staffKey || 'Not specified';
  };

  const getServiceName = (serviceKey) => {
    const service = services.find(s => s.key === serviceKey);
    return service?.name || serviceKey || 'Unknown Service';
  };

  const getServiceColor = (serviceKey) => {
    return 'blue'; // Use consistent brand color
  };

  const filteredBookings = bookings.filter(b => 
    statusFilter === 'all' || b.status === statusFilter
  );

  const upcomingBookings = filteredBookings.filter(b => 
    new Date(b.scheduled_date) >= new Date() && b.status !== 'cancelled'
  );

  const pastBookings = filteredBookings.filter(b => 
    new Date(b.scheduled_date) < new Date() || b.status === 'cancelled'
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-gray-500 animate-pulse">Loading bookings...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="bookings-page">
      {/* Header */}
      <div className="bg-zinc-950 shadow-sm border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/')}
                className="text-gray-400 hover:text-white"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                  <Calendar className="h-8 w-8 text-[#02b5ff]" />
                  Bookings
                </h1>
                <p className="text-gray-400 mt-1">
                  Manage training sessions and appointments
                  {integrationStatus && !integrationStatus.configured && (
                    <span className="ml-2 text-xs text-[#02b5ff] bg-[#02b5ff]/10 px-2 py-0.5 rounded-full">
                      Local Mode
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <a
                href="https://eliteplayerperformance.setmore.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="h-12 px-6 bg-[#02b5ff] hover:bg-[#0099dd] text-white font-semibold rounded-full shadow-lg flex items-center gap-2 transition-colors"
                data-testid="book-services-btn"
              >
                <Calendar className="h-5 w-5" />
                Book Services
              </a>
              <Button
                onClick={() => setShowModal(true)}
                className="h-12 px-6 bg-gradient-to-r from-[#02b5ff] to-[#ff025f] hover:opacity-90 text-white font-semibold rounded-full shadow-lg"
              >
                <Plus className="mr-2 h-5 w-5" />
                New Booking
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-zinc-900 rounded-2xl p-6 shadow-md border border-zinc-800">
            <div className="text-3xl font-bold text-white">{bookings.length}</div>
            <div className="text-sm text-gray-400">Total Bookings</div>
          </div>
          <div className="bg-zinc-900 rounded-2xl p-6 shadow-md border border-zinc-800">
            <div className="text-3xl font-bold text-[#02b5ff]">{upcomingBookings.length}</div>
            <div className="text-sm text-gray-400">Upcoming</div>
          </div>
          <div className="bg-zinc-900 rounded-2xl p-6 shadow-md border border-zinc-800">
            <div className="text-3xl font-bold text-[#02b5ff]">
              {bookings.filter(b => b.status === 'confirmed').length}
            </div>
            <div className="text-sm text-gray-400">Confirmed</div>
          </div>
          <div className="bg-zinc-900 rounded-2xl p-6 shadow-md border border-zinc-800">
            <div className="text-3xl font-bold text-[#ff025f]">
              {bookings.filter(b => b.status === 'cancelled').length}
            </div>
            <div className="text-sm text-gray-400">Cancelled</div>
          </div>
        </div>

        {/* Filter */}
        <div className="flex items-center gap-4 mb-6">
          <Filter className="h-5 w-5 text-gray-400" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 bg-white border-zinc-700 rounded-xl">
              <SelectValue placeholder="Filter status" />
            </SelectTrigger>
            <SelectContent className="bg-white border-zinc-700">
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Upcoming Bookings */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-white mb-4">Upcoming Sessions</h2>
          {upcomingBookings.length === 0 ? (
            <div className="bg-zinc-900 rounded-2xl p-8 shadow-md border border-zinc-800 text-center">
              <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No upcoming bookings</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {upcomingBookings.map((booking) => (
                <div 
                  key={booking.id}
                  className="bg-zinc-900 rounded-2xl p-6 shadow-md border border-zinc-800 hover:shadow-lg transition-all"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="px-3 py-1 rounded-full text-xs font-semibold bg-[#02b5ff]/10 text-[#02b5ff]">
                      {booking.service_name || booking.service_type || 'Session'}
                    </div>
                    <span className="flex items-center gap-1 text-xs text-[#02b5ff]">
                      <CheckCircle className="h-3 w-3" />
                      {booking.status}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2 mb-3">
                    <User className="h-4 w-4 text-gray-400" />
                    <span className="font-semibold text-white">{getChildName(booking.child_id)}</span>
                  </div>
                  
                  <div className="flex items-center gap-2 mb-2 text-sm text-gray-400">
                    <Calendar className="h-4 w-4" />
                    {new Date(booking.scheduled_date).toLocaleDateString('en-GB', {
                      weekday: 'short', day: 'numeric', month: 'short'
                    })}
                  </div>
                  
                  <div className="flex items-center gap-2 mb-4 text-sm text-gray-400">
                    <Clock className="h-4 w-4" />
                    {new Date(booking.scheduled_date).toLocaleTimeString('en-GB', {
                      hour: '2-digit', minute: '2-digit'
                    })} ({booking.duration_minutes} min)
                  </div>
                  
                  {(booking.staff_name || booking.staff_key || booking.staff_location) && (
                    <div className="flex items-center gap-2 mb-4 text-sm text-gray-400">
                      <MapPin className="h-4 w-4" />
                      {booking.staff_name || getLocationName(booking.staff_key) || getLocationName(booking.staff_location)}
                    </div>
                  )}
                  
                  {booking.setmore_synced && (
                    <div className="mb-3 text-xs text-[#02b5ff] flex items-center gap-1">
                      <CheckCircle className="h-3 w-3" />
                      Synced to Setmore
                    </div>
                  )}
                  
                  {booking.notes && (
                    <p className="text-xs text-gray-500 mb-4 line-clamp-2">{booking.notes}</p>
                  )}
                  
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => cancelBooking(booking.id)}
                    className="w-full text-[#ff025f] border-[#ff025f]/30 hover:bg-[#ff025f]/10 rounded-xl"
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Past Bookings */}
        {pastBookings.length > 0 && (
          <div>
            <h2 className="text-xl font-bold text-white mb-4">Past Sessions</h2>
            <div className="bg-zinc-900 rounded-2xl shadow-md border border-zinc-800 overflow-hidden">
              <table className="w-full">
                <thead className="bg-zinc-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400">Athlete</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400">Service</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {pastBookings.slice(0, 10).map((booking) => (
                    <tr key={booking.id} className="hover:bg-zinc-800">
                      <td className="px-6 py-4 text-sm text-white">{getChildName(booking.child_id)}</td>
                      <td className="px-6 py-4 text-sm text-gray-400">
                        {SERVICE_TYPES.find(s => s.value === booking.service_type)?.label || booking.service_type}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-400">
                        {new Date(booking.scheduled_date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          booking.status === 'cancelled' ? 'bg-[#ff025f]/10 text-[#ff025f]' : 'bg-gray-100 text-gray-300'
                        }`}>
                          {booking.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Create Booking Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-zinc-900 rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-white mb-6">New Booking</h2>
            {integrationStatus?.configured && (
              <div className="mb-4 p-3 bg-[#02b5ff]/10 border border-[#02b5ff]/30 rounded-xl text-sm text-[#02b5ff]">
                ✓ Syncing with Setmore
              </div>
            )}
            <form onSubmit={createBooking} className="space-y-4">
              <div>
                <Label className="text-sm font-semibold text-gray-300">Athlete</Label>
                <Select value={formData.child_id} onValueChange={(v) => setFormData({...formData, child_id: v})}>
                  <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700 rounded-xl">
                    <SelectValue placeholder="Select athlete" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-zinc-700 max-h-60">
                    {children.map(child => (
                      <SelectItem key={child.id} value={child.id}>
                        {child.first_name} {child.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-sm font-semibold text-gray-300">Location</Label>
                <Select value={formData.staff_key} onValueChange={(v) => setFormData({...formData, staff_key: v})}>
                  <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700 rounded-xl">
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-zinc-700 max-h-60">
                    {staffLocations.map(loc => (
                      <SelectItem key={loc.key} value={loc.key}>
                        <div className="flex flex-col">
                          <span className="font-medium">{loc.name}</span>
                          {loc.address && <span className="text-xs text-gray-500 truncate max-w-[250px]">{loc.address}</span>}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-sm font-semibold text-gray-300">Service</Label>
                <Select value={formData.service_key} onValueChange={(v) => setFormData({...formData, service_key: v})}>
                  <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700 rounded-xl">
                    <SelectValue placeholder="Select service" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-zinc-700 max-h-60">
                    {services.map(service => (
                      <SelectItem key={service.key} value={service.key}>
                        <div className="flex flex-col">
                          <span className="font-medium">{service.name}</span>
                          <span className="text-xs text-gray-500">
                            {service.duration} min • {service.cost === 0 ? 'Free' : `${service.currency} ${service.cost}`}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-semibold text-gray-300">Date</Label>
                  <Input
                    type="date"
                    value={formData.scheduled_date}
                    onChange={(e) => setFormData({...formData, scheduled_date: e.target.value})}
                    className="mt-1 bg-zinc-800 border-zinc-700 rounded-xl"
                    required
                  />
                </div>
                <div>
                  <Label className="text-sm font-semibold text-gray-300">Time</Label>
                  <Input
                    type="time"
                    value={formData.scheduled_time}
                    onChange={(e) => setFormData({...formData, scheduled_time: e.target.value})}
                    className="mt-1 bg-zinc-800 border-zinc-700 rounded-xl"
                    required
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-sm font-semibold text-gray-300">Contact Email (optional)</Label>
                <Input
                  type="email"
                  value={formData.customer_email}
                  onChange={(e) => setFormData({...formData, customer_email: e.target.value})}
                  className="mt-1 bg-zinc-800 border-zinc-700 rounded-xl"
                  placeholder="athlete@email.com"
                />
              </div>
              
              <div>
                <Label className="text-sm font-semibold text-gray-300">Contact Phone (optional)</Label>
                <Input
                  value={formData.customer_phone}
                  onChange={(e) => setFormData({...formData, customer_phone: e.target.value})}
                  className="mt-1 bg-zinc-800 border-zinc-700 rounded-xl"
                  placeholder="07xxx xxxxxx"
                />
              </div>
              
              <div>
                <Label className="text-sm font-semibold text-gray-300">Notes (optional)</Label>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="mt-1 bg-zinc-800 border-zinc-700 rounded-xl"
                  placeholder="Any special instructions..."
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowModal(false)}
                  className="flex-1 rounded-xl"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!formData.child_id || !formData.scheduled_date || !formData.scheduled_time || !formData.service_key || !formData.staff_key}
                  className="flex-1 bg-[#02b5ff] hover:bg-[#0099dd] text-white rounded-xl"
                >
                  Create Booking
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
